import { useCustomFieldAndObjectRoutes } from '@oc/pages/settings/CustomFieldsAndObjects/utils/useCustomFieldsAndObjectRoutes';
import { useGetNavItems as enterpriseNavItems } from '@oc/pages/settings/Enterprise/EnterpriseHome.constants';
import { useMonitoringNavItems } from '@oc/pages/settings/monitoring/useMonitoringNavItems';
import { useAllowedProcessesNavItems } from '@oc/pages/settings/Processes/Processes.constants';
import { useNavItems as useSftpNavItems } from '@oc/pages/settings/Sftp/SftpSettings.constants';
import { useNavItems as useSourcingNavItems } from '@oc/pages/settings/Sourcing/Tabs';
import { useTenantNavItems } from '@oc/pages/settings/tenant/useTenantNavItems';
import { useGetInterfaceTabs } from '@oc/pages/settings/userInterfaceSettings/useGetInterfaceTabs';
import { useGetNavItems as useUserManagmentNavItems } from '@oc/pages/settings/UserManagement/UserManagement.constants';
import { useGetNavItems as useWorkersNavItems } from '@oc/pages/settings/Workers/Workers.constants';
import { isSettingVisible } from 'common/auth/Authorize';
import {
  SecondaryNavGroup,
  SecondaryNavItem
} from 'src/components/common/SecondaryNavigation/SecondaryNavigation.types';
import { useLoggedInUser } from 'utils/hooks/useLoggedInUser';
import { useSettingsIntl } from './common/translations/useSettingsIntl';
import { filterNotEmpty } from '@beeline.com/bp-design-system';
import { SETTINGS_TAB } from './Tabs.types';
import { PermissionAction, PermissionName } from 'src/graphql/types';
import { LaunchDarklyFeatureFlagKeys } from 'integrations/launchDarkly/legacy/LaunchDarklyFeatureFlags.types';
import { useDarklyGlobal } from 'integrations/launchDarkly/legacy/useDarklyGlobal';
import { FeaturesNames } from 'src/definitions/custom-models';
import { useEngagementRoutes } from './Engagements/useEngagementRoutes';

export const useGetSettingsNavItems = ({
  isProgramAlertsEnabled,
  tae,
  invoices,
  invoicesV2,
  newInvoicesSettingsPage
}: {
  isProgramAlertsEnabled: boolean;
  tae: boolean;
  invoices: boolean;
  invoicesV2: boolean;
  newInvoicesSettingsPage: boolean | undefined;
}): (Omit<SecondaryNavItem, 'component'> | SecondaryNavGroup)[] => {
  const intl = useSettingsIntl();
  const userManagementRoutes = useUserManagmentNavItems().navItems;
  const workersRoutes = useWorkersNavItems().navItems;
  const globalDarkly = useDarklyGlobal();
  const customObjectsRoutes = useCustomFieldAndObjectRoutes();
  const enterpriseRoutes = enterpriseNavItems().navItems;
  const engagementRoutes = useEngagementRoutes();
  const processesRoutes = useAllowedProcessesNavItems();
  const sourcingRoutes = useSourcingNavItems();
  const userInterfaceRoutes = useGetInterfaceTabs().getTabs();
  const monitoringRoutes = useMonitoringNavItems().navItems;
  const tenantRoutes = useTenantNavItems().navItems;
  const sftpRoutes = useSftpNavItems();
  const authUser = useLoggedInUser();
  if (!authUser || !isSettingVisible(authUser)) {
    return [];
  }

  return filterNotEmpty([
    {
      label: intl.userManagement,
      path: SETTINGS_TAB.USER_MANAGEMENT,
      'data-test': 'user-management',
      permissions: [{ name: PermissionName.SettingsUser }],
      exact: false,
      searchableSubItems: userManagementRoutes
    },
    {
      label: intl.eligibilityProvisioning,
      path: SETTINGS_TAB.ELIGIBILITY_AND_PROVISIONING,
      'data-test': 'eligibility-and-provisioning',
      useDarkly: LaunchDarklyFeatureFlagKeys.complianceCertificationManagement,
      exact: false,
      permissions: [{ name: PermissionName.EligibilityRequirement }]
    },
    isProgramAlertsEnabled && {
      label: intl.programAlerts,
      path: SETTINGS_TAB.COMPLIANCE_AND_RISK_ALERTS,
      // TODO: Enable after migration
      // featureFlag: 'program-alerts',
      'data-test': 'program-alerts__settings__tab',
      permissions: [{ name: PermissionName.SettingsAnomalies }, { name: PermissionName.ComplianceDefinition }],
      exact: false
    },
    {
      label: intl.businessSites,
      path: SETTINGS_TAB.BUSINESS_SITES,
      'data-test': 'locations__tab',
      permissions: [{ name: PermissionName.SettingsLocation }],
      exact: true
    },
    {
      label: intl.securityPermissions,
      path: SETTINGS_TAB.SECURITY_PERMISSIONS,
      'data-test': 'security__permissions__tab',
      permissions: [{ name: PermissionName.SettingsPermissions }],
      exact: false
    },
    {
      label: intl.mspManagement,
      path: SETTINGS_TAB.MSP_MANAGEMENT,
      'data-test': 'msp__management__tab',
      permissions: [{ name: PermissionName.Msp }],
      featureFlag: 'msp' as FeaturesNames,
      exact: false
    },
    {
      label: intl.classifications,
      path: SETTINGS_TAB.CLASSIFICATIONS,
      'data-test': 'classifications__tab',
      permissions: [{ name: PermissionName.SettingsClassification }],
      exact: true
    },
    {
      label: intl.companies,
      path: SETTINGS_TAB.COMPANIES,
      'data-test': 'companies__tab',
      permissions: [{ name: PermissionName.Company }],
      exact: true
    },
    {
      label: intl.objectsAndFields,
      path: SETTINGS_TAB.CUSTOM_FIELDS_AND_OBJECTS,
      'data-test': 'custom_fields-and-objects__tab',
      permissions: [{ name: PermissionName.SettingsCustomFields }],
      exact: false,
      searchableSubItems: customObjectsRoutes
    },
    {
      label: intl.businessEvents,
      path: SETTINGS_TAB.BUSINESS_EVENTS,
      'data-test': 'business-events__tab',
      permissions: [{ name: PermissionName.SettingsConfiguration }],
      exact: false
    },
    {
      label: intl.enterprise,
      path: SETTINGS_TAB.ENTERPRISE,
      'data-test': 'enterprise__tab',
      permissions: [{ name: PermissionName.SettingsEnterprise }],
      exact: false,
      searchableSubItems: enterpriseRoutes
    },
    {
      label: intl.engagements,
      path: SETTINGS_TAB.ENGAGEMENTS,
      'data-test': 'engagements__tab',
      permissions: [{ name: PermissionName.SettingsEngagements }],
      exact: false,
      searchableSubItems: engagementRoutes
    },
    globalDarkly.configureWorkerIdPrefixViaSettingsUi && {
      label: intl.workers,
      path: SETTINGS_TAB.WORKERS,
      'data-test': 'workers__tab',
      permissions: [{ name: PermissionName.SettingsConfiguration }],
      exact: false,
      searchableSubItems: workersRoutes,
      featureFlag: 'workers-configuration' as FeaturesNames
    },
    {
      label: intl.processes,
      path: SETTINGS_TAB.PROCESSES,
      'data-test': 'processes',
      permissions: [{ name: PermissionName.ProcessDefinition }],
      featureFlag: 'processes' as FeaturesNames,
      exact: false,
      searchableSubItems: processesRoutes
    },
    {
      label: intl.contracts,
      path: SETTINGS_TAB.CONTRACTS,
      'data-test': 'contracts',
      permissions: [{ name: PermissionName.Contract }],
      exact: false
    },
    tae && {
      label: intl.spend,
      groupPath: SETTINGS_TAB.SPEND_SETTINGS,
      items: filterNotEmpty([
        {
          label: intl.general,
          path: SETTINGS_TAB.SPEND_GENERAL,
          'data-test': 'spend_general__tab',
          permissions: [{ name: PermissionName.SettingsSpend }],
          exact: false
        },
        {
          label: intl.timesheetSettings,
          path: SETTINGS_TAB.TIMESHEET,
          'data-test': 'timesheet_settings_new__tab',
          permissions: [{ name: PermissionName.Timesheet }],
          exact: false
        },
        {
          label: intl.financialFields,
          path: SETTINGS_TAB.FINANCIAL_FIELDS,
          'data-test': 'financial_fields__tab',
          permissions: [{ name: PermissionName.SettingsFinancialFields }],
          exact: false
        },
        invoices && {
          label: intl.invoices,
          groupPath: SETTINGS_TAB.INVOICES,
          items: filterNotEmpty([
            newInvoicesSettingsPage && {
              label: intl.invoiceGeneral,
              path: SETTINGS_TAB.INVOICE_GENERAL,
              'data-test': 'invoice_general__tab',
              permissions: [{ name: PermissionName.InvoicingBlueprintDefinition }],
              exact: false
            },
            invoicesV2 && {
              label: intl.invoiceBlueprints,
              path: SETTINGS_TAB.INVOICE_BLUEPRINTS,
              'data-test': 'invoice_blueprints__tab',
              permissions: [{ name: PermissionName.InvoicingBlueprintDefinition }],
              exact: false
            }
          ])
        }
      ])
    },
    {
      label: intl.smartForms,
      path: SETTINGS_TAB.SMART_FORMS,
      'data-test': 'smart_form__tab',
      permissions: [{ name: PermissionName.SettingsSmartForms, action: PermissionAction.Read }],
      featureFlag: 'smart-forms' as FeaturesNames,
      exact: false
    },
    {
      label: intl.integrations,
      groupPath: SETTINGS_TAB.INTEGRATIONS,
      permissions: [{ name: PermissionName.SettingsIntegrations, action: PermissionAction.Read }],
      items: [
        {
          label: intl.apiKeys,
          path: SETTINGS_TAB.API_KEYS,
          'data-test': 'settings_api_keys',
          permissions: [{ name: PermissionName.SettingsClientIds, action: PermissionAction.Read }]
        },
        {
          label: intl.ssoIntegration,
          path: SETTINGS_TAB.SSO_INTEGRATIONS,
          'data-test': 'settings_sso_integrations',
          permissions: [{ name: PermissionName.SettingsSso, action: PermissionAction.Read }]
        },
        {
          label: intl.connectors,
          path: SETTINGS_TAB.CONNECTORS,
          'data-test': 'settings_connectors',
          useDarkly: LaunchDarklyFeatureFlagKeys.externalIntegrations
        },
        {
          label: intl.recipes,
          path: SETTINGS_TAB.RECIPES,
          'data-test': 'recipes',
          permissions: [{ name: PermissionName.Recipe, action: PermissionAction.Read }],
          featureFlag: 'recipes' as FeaturesNames
        }
      ]
    },
    {
      label: intl.sourcing,
      path: SETTINGS_TAB.SOURCING,
      'data-test': 'sourcing',
      permissions: [{ name: PermissionName.SettingsUser }],
      exact: false,
      searchableSubItems: sourcingRoutes
    },
    {
      label: intl.fieldMetadataConstraints,
      path: SETTINGS_TAB.FIELD_METADATA,
      'data-test': 'field-metadata',
      permissions: [{ name: PermissionName.FieldMetadataEditing }],
      exact: false
    },
    {
      label: intl.userInterfaceSettings,
      path: SETTINGS_TAB.USER_INTERFACE_SETTINGS,
      'data-test': 'settings_user_interface',
      permissions: [{ name: PermissionName.SettingsConfiguration }],
      exact: false,
      searchableSubItems: userInterfaceRoutes
    },
    {
      label: intl.monitoring,
      path: SETTINGS_TAB.MONITORING,
      'data-test': 'monitoring',
      exact: false,
      searchableSubItems: monitoringRoutes
    },
    {
      label: intl.tenantConfiguration,
      path: SETTINGS_TAB.TENANTS,
      permissions: [{ name: PermissionName.TenantConfig }],
      'data-test': 'tenant-config',
      exact: false,
      searchableSubItems: tenantRoutes
    },
    {
      label: intl.sftp,
      path: SETTINGS_TAB.SFTP,
      'data-test': 'sftp',
      exact: false,
      searchableSubItems: sftpRoutes
    },
    {
      label: intl.trendingAnalytics,
      path: SETTINGS_TAB.ANALYTICS_DASHBOARDS,
      'data-test': 'analytics_dashboards',
      exact: false,
      useDarkly: LaunchDarklyFeatureFlagKeys.analyticsDashboards
    },
    {
      label: intl.about,
      path: SETTINGS_TAB.ABOUT_US,
      'data-test': 'about_us',
      exact: false
    },
    {
      label: intl.devTools,
      path: SETTINGS_TAB.DEV_TOOLS,
      'data-test': 'dev_tools',
      exact: false,
      useDarkly: LaunchDarklyFeatureFlagKeys.devTools
    }
  ]);
};
